import { Component, signal, OnInit, inject } from '@angular/core';
import { FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import {
  Exception,
  StorageService,
  StorageType,
  ToasterService,
  Validator,
} from '@anddone/ui-utilities';
import { ValidationConstant } from '../../../../../constants/validation.constant';
import { UserService } from '../../../../../services/user-service';
import { AllyCommonService } from '../../../../../services/ally-common-service';
import { MessageConstants } from '../../../../../constants/message.constant';

@Component({
  selector: 'app-roll-api',
  imports: [FormsModule, ReactiveFormsModule, TranslateModule, NgClass],
  templateUrl: './roll-api.html',
  styleUrl: './roll-api.scss',
})
export class RollApi implements OnInit {
  rollApiKeyForm: any;
  rollApiKeyFormErrors: any = {};
  validator: Validator;
  loggedInUserData: any;
  keyData: any;
  isLoader = signal<boolean>(false);
  passwordFieldType = 'password';
  config = {
    password: {
      required: { name: ValidationConstant.rollApiKey.password.name },
      minlength: {
        name: ValidationConstant.rollApiKey.password.name,
        min: ValidationConstant.rollApiKey.password.minLength.toString(),
      },
    },
    note: {
      required: { name: ValidationConstant.rollApiKey.note.name },
      minlength: {
        name: ValidationConstant.rollApiKey.note.name,
        min: ValidationConstant.rollApiKey.note.minLength.toString(),
      },
    },
  };

  private formBuilder = inject(FormBuilder);
  public activeModal = inject(NgbActiveModal);
  public allyCommonService = inject(AllyCommonService);
  private storageService = inject(StorageService);
  public userService = inject(UserService);
  public toasterService = inject(ToasterService);
  constructor() {
    this.validator = new Validator(this.config);
  }

  ngOnInit(): void {
    this.keyData = this.userService.getAPIKey();
    const newAPIKey = this.keyData.source._value[0]?.keyValue;
    this.rollApiKeyForm = this.formBuilder.group({
      key: [newAPIKey, []],
      note: [
        '',
        [Validators.required, Validators.minLength(ValidationConstant.rollApiKey.note.minLength)],
      ],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(ValidationConstant.rollApiKey.password.minLength),
        ],
      ],
    });

    this.rollApiKeyForm.valueChanges.subscribe((data: any) => this.onValueChanged(data));

    this.loggedInUserData = JSON.parse(
      this.storageService.get(StorageType.Session, 'userDetails') ?? '{}'
    );
  }

  // form on change value
  onValueChanged(_data?: any) {
    if (!this.rollApiKeyForm) {
      return;
    }
    this.rollApiKeyFormErrors = this.validator.validate(this.rollApiKeyForm);
  }

  // Submitting the roll API key form
  onSubmit() {
    this.validator.validateAllFormFields(this.rollApiKeyForm);
    this.rollApiKeyFormErrors = this.validator.validate(this.rollApiKeyForm);
    if (this.rollApiKeyForm.invalid) {
      return;
    }
    if (this.checkSpaces(this.rollApiKeyForm.value.note)) {
      this.toasterService.showError(MessageConstants.rollAPI.allyKeyCommentCannotBeEmpty, 'full');
      return;
    }
    const obj = {
      comment: this.rollApiKeyForm.value.note,
      password: this.rollApiKeyForm.value.password,
      isImmediateExpire: false,
      userName: this.loggedInUserData.adminUserName,
    };
    this.isLoader.set(true);
    this.userService
      .rollApiKey(obj, this.keyData.source._value[0].userId, this.loggedInUserData.id)
      .subscribe({
        next: (_res: any) => {
          this.rollApiKeyForm.reset();
          this.isLoader.set(false);
          this.activeModal.close('Modal closed');
          this.toasterService.showSuccess(MessageConstants.rollAPI.apiKeyGenerated, 'full');
          this.userService.updateRollAPIKeyValue(true);
        },
        error: (e) => {
          this.isLoader.set(false);
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        },
      });
  }

  //make password visible
  onTogglePassword() {
    this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
  }

  close() {
    this.activeModal.close();
  }

  checkSpaces(input: string): boolean {
    // Check if the string consists only of spaces (whitespace characters)
    return /^[\s]*$/.test(input);
  }
}
